/**
 * An MCP app resource on a turn (a `ui-resource` block, D372), DOM-free: what it holds and how a screen may show it. HTML
 * runs only in a sandboxed frame of its own (never in the page's document); a `text/uri-list` names a page to open; any
 * other kind is named, with its text when it has some. The rules are the workbench's McpAppFrame
 * (webview-ui/src/components/facilitator/McpAppFrame.tsx, 0.90.0): the base mime type, the body from `text` or the
 * base64 `blob`, the first http(s) line of a uri-list.
 */
/** The mime type without its parameters (`;profile=mcp-app`), lower case. */
export function baseMime(mimeType) {
    return (mimeType.split(";")[0] ?? "").trim().toLowerCase();
}
export function isHtmlMime(mimeType) {
    const base = baseMime(mimeType);
    return base === "text/html" || base === "text/html+skybridge";
}
/** UTF-8 from base64, without Node or the DOM's TextDecoder being required. */
function decodeBase64(b64) {
    try {
        const bin = typeof atob === "function" ? atob(b64) : "";
        const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
        if (typeof TextDecoder === "function")
            return new TextDecoder().decode(bytes);
        return decodeURIComponent(Array.from(bytes, (b) => `%${b.toString(16).padStart(2, "0")}`).join(""));
    }
    catch {
        return "";
    }
}
/** The resource's content, whichever encoding the server chose. */
export function uiResourceBody(block) {
    if (typeof block.text === "string" && block.text.length > 0)
        return block.text;
    if (typeof block.blob === "string" && block.blob)
        return decodeBase64(block.blob);
    return "";
}
/** How a resource is shown; undefined for a uri-list with no page in it (nothing to show). */
export function uiResourceView(block) {
    const body = uiResourceBody(block);
    if (baseMime(block.mimeType) === "text/uri-list") {
        const url = body
            .split("\n")
            .map((l) => l.trim())
            .find((l) => /^https?:\/\//i.test(l));
        return url ? { kind: "link", url, uri: block.uri } : undefined;
    }
    if (isHtmlMime(block.mimeType))
        return { kind: "html", html: body, uri: block.uri };
    return { kind: "other", uri: block.uri, mimeType: block.mimeType, body };
}
/** What a request from an app's frame asking for a tool says to the agent: relayed into the conversation, never run. */
export function uiResourceToolLine(name, args) {
    const json = args ? `\n\n\`\`\`json\n${JSON.stringify(args, null, 2)}\n\`\`\`` : "";
    return `The embedded app requested the \`${name}\` tool.${json}`;
}
//# sourceMappingURL=uiResourceModel.js.map